import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { parseDateAndTime } from '@/lib/dateTimeUtils';
import { PLATFORMS } from '@/lib/platformConstants';

export interface ToastPayoutRow {
  location: string;
  settledDate: string;
  batchDates: string;
  salesDates: string;
  batchNumbers: string;
  firstTxDate: string | null;
  lastTxDate: string | null;
  totalNumberPayments: number;
  totalPaymentsAmount: number;
  totalTips: number;
  totalGiftCardSales: number;
  totalAmexTips: number;
  totalCashTips: number;
  withholding: number;
  toastCollectedTips: number;
  transactionFees: number;
  netDepositAmount: number;
}

export interface ProcessedToastPayoutTransaction {
  platform: string;
  source: string;
  location: string;
  settled_date: string | null;
  batch_dates: string;
  sales_dates: string;
  batch_numbers: string;
  first_tx_date: string | null;
  last_tx_date: string | null;
  total_number_of_payments: number;
  total_payments_amount: number;
  total_tips: number;
  total_gift_card_sales: number;
  total_amex_tips: number;
  total_cash_tips: number;
  withholding: number;
  toast_collected_tips: number;
  transaction_fees: number;
  net_deposit_amount: number;
}

const parseNumeric = (value: any): number => {
  if (value === null || value === undefined || value === '') return 0;
  const num = typeof value === 'string' ? parseFloat(value.replace(/[,$]/g, '')) : Number(value);
  return isNaN(num) ? 0 : num;
};

const parseDate = (value: any): string | null => {
  if (!value) return null;
  const cleaned = typeof value === 'string'
    ? value.replace(/\s+(Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)/i, '')
    : value;
  const parsed = parseDateAndTime(cleaned);
  return parsed.date;
};

/**
 * Detect if this is a Toast Payouts file from headers.
 * Supports two formats:
 * - Old XLSX: Location, Settled Date + Batch Date, Sales Date, Transaction Fees, Net Deposit, Total Payments
 * - New CSV: Settled date, Location + Payments, Fees, Payout, # Txns
 */
export function detectToastPayoutsFromHeaders(headers: any[]): boolean {
  const filteredHeaders = headers.filter(h => h && String(h).trim() !== '');
  const headerStr = filteredHeaders.map(h => String(h || '').toLowerCase().trim()).join('|');
  
  console.log('Toast Payouts detection - Filtered headers:', headerStr.substring(0, 150));
  
  const hasLocation = headerStr.includes('location');
  const hasSettledDate = headerStr.includes('settled date');
  
  const optionalOld = ['batch date', 'sales date', 'total payments', 'transaction fees', 'net deposit'];
  const optionalNew = ['payments', 'fees', 'payout', '# txns'];
  const optionalOldCount = optionalOld.filter(col => headerStr.includes(col)).length;
  const optionalNewCount = optionalNew.filter(col => headerStr.includes(col)).length;
  
  return hasLocation && hasSettledDate && (optionalOldCount >= 2 || optionalNewCount >= 2);
}

type FormatVariant = 'old_xlsx' | 'new_csv';

function detectFormat(headerRow: any[]): FormatVariant {
  const headerStr = headerRow.map(h => String(h || '').toLowerCase().trim()).join('|');
  if (headerStr.includes('batch date') || headerStr.includes('net deposit')) {
    return 'old_xlsx';
  }
  return 'new_csv';
}

/**
 * Parse old XLSX format (18 columns with batch-level detail).
 * A: (batch ID), B: Location, C: Settled Date, D-F: Batch/Sales dates, G-H: Tx dates,
 * I: # Payments, J: Total Payments, K: Tips, L-N: Gift/Amex/Cash, O: Withholding,
 * P: Toast Collected Tips, Q: Transaction Fees, R: Net Deposit Amount
 */
function parseOldFormat(rawRows: any[][]): ProcessedToastPayoutTransaction[] {
  const transactions: ProcessedToastPayoutTransaction[] = [];
  
  for (let i = 1; i < rawRows.length; i++) {
    const row = rawRows[i];
    if (!row || !row[1]) continue;
    
    transactions.push({
      platform: PLATFORMS.TOAST,
      source: 'Toast YTD Payouts',
      location: String(row[1] || '').trim(),
      settled_date: parseDate(row[2]),
      batch_dates: String(row[3] || ''),
      sales_dates: String(row[4] || ''),
      batch_numbers: String(row[5] || ''),
      first_tx_date: parseDate(row[6]),
      last_tx_date: parseDate(row[7]),
      total_number_of_payments: parseNumeric(row[8]),
      total_payments_amount: parseNumeric(row[9]),
      total_tips: parseNumeric(row[10]),
      total_gift_card_sales: parseNumeric(row[11]),
      total_amex_tips: parseNumeric(row[12]),
      total_cash_tips: parseNumeric(row[13]),
      withholding: parseNumeric(row[14]),
      toast_collected_tips: parseNumeric(row[15]),
      transaction_fees: parseNumeric(row[16]),
      net_deposit_amount: parseNumeric(row[17]),
    });
  }
  
  return transactions;
}

/**
 * Parse new CSV format (14 columns, Payout Overview export).
 * Settled date, Name, Location, Type, Sales period start, Sales period end,
 * # Txns, Payments, Refunds, Fees, Withholdings, External, Payout, Status
 */
function parseNewCsvFormat(rawRows: any[][]): ProcessedToastPayoutTransaction[] {
  const transactions: ProcessedToastPayoutTransaction[] = [];
  const headerRow = rawRows[0] || [];
  
  const headerMap: Record<string, number> = {};
  headerRow.forEach((h: any, idx: number) => {
    if (h != null) headerMap[String(h).toLowerCase().trim()] = idx;
  });

  const col = (name: string, fallback: number): number => headerMap[name] ?? fallback;
  const colSettledDate = col('settled date', 0);
  const colLocation = col('location', 2);
  const colType = col('type', 3);
  const colSalesPeriodStart = col('sales period start', 4);
  const colSalesPeriodEnd = col('sales period end', 5);
  const colNumTxns = col('# txns', 6);
  const colPayments = col('payments', 7);
  const colRefunds = col('refunds', 8);
  const colFees = col('fees', 9);
  const colWithholdings = col('withholdings', 10);
  const colPayout = col('payout', 12);

  for (let i = 1; i < rawRows.length; i++) {
    const row = rawRows[i];
    if (!row) continue;
    
    const settledDateRaw = row[colSettledDate];
    const locationRaw = row[colLocation];
    if (!settledDateRaw && !locationRaw) continue;

    const settledDate = parseDate(settledDateRaw);
    const salesStart = String(row[colSalesPeriodStart] || '');
    const salesEnd = String(row[colSalesPeriodEnd] || '');
    const salesDates = salesStart && salesEnd ? `${salesStart} - ${salesEnd}` : salesStart || salesEnd;

    transactions.push({
      platform: PLATFORMS.TOAST,
      source: 'Toast YTD Payouts',
      location: String(locationRaw || '').trim(),
      settled_date: settledDate,
      batch_dates: '',
      sales_dates: salesDates,
      batch_numbers: '',
      first_tx_date: null,
      last_tx_date: null,
      total_number_of_payments: parseNumeric(row[colNumTxns]),
      total_payments_amount: parseNumeric(row[colPayments]),
      total_tips: 0,
      total_gift_card_sales: 0,
      total_amex_tips: 0,
      total_cash_tips: 0,
      withholding: parseNumeric(row[colWithholdings]),
      toast_collected_tips: 0,
      transaction_fees: parseNumeric(row[colFees]),
      net_deposit_amount: parseNumeric(row[colPayout]),
    });
  }

  return transactions;
}

/**
 * Process Toast Payouts file. Supports both old XLSX and new CSV format.
 */
export const processToastPayoutsFile = async (file: File): Promise<ProcessedToastPayoutTransaction[]> => {
  try {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    let rawRows: any[][];

    if (fileExtension === 'csv') {
      const text = await file.text();
      const result = Papa.parse(text, { header: false });
      rawRows = result.data as any[][];
    } else {
      const arrayBuffer = await file.arrayBuffer();
      const data = new Uint8Array(arrayBuffer);
      const workbook = XLSX.read(data, { type: 'array' });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      rawRows = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];
    }
    
    const headers = rawRows[0] || [];
    
    if (!detectToastPayoutsFromHeaders(headers)) {
      throw new Error('File does not appear to be a Toast Payouts report. Expected headers: Location, Settled Date, etc.');
    }
    
    const format = detectFormat(headers);
    console.log(`Toast Payouts - Format: ${format}, Headers: ${headers.slice(0, 10).join(', ')}`);
    console.log(`Toast Payouts - Raw data rows: ${rawRows.length - 1}`);

    const transactions = format === 'old_xlsx'
      ? parseOldFormat(rawRows)
      : parseNewCsvFormat(rawRows);

    console.log('Toast Payouts - Processed transactions:', transactions.length);
    if (transactions.length > 0) {
      console.log('Toast Payouts - Sample transaction:', transactions[0]);
    }

    return transactions;
  } catch (error) {
    console.error('Error processing Toast Payouts file:', error);
    throw error;
  }
};
